import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

const EMPTY = {
  specification: "", size: "", price: "", pcs_per_ctn: "", cbm: "", upc: "", qty: "",
  carton_length: "", carton_width: "", carton_height: "", purchase_price: "", gross_weight: "",
};

const FIELDS = [
  ["specification", "Specification"],
  ["size", "Size"],
  ["price", "Price (USD)", "number"],
  ["pcs_per_ctn", "Pcs/Ctn", "number"],
  ["upc", "UPC"],
  ["qty", "QTY", "number"],
  ["carton_length", "Carton Length (cm)", "number"],
  ["carton_width", "Carton Width (cm)", "number"],
  ["carton_height", "Carton Height (cm)", "number"],
  ["purchase_price", "Factory Cost (USD)", "number"],
  ["gross_weight", "Gross Weight (KG)", "number"],
];

export default function VariantFormDialog({ open, onOpenChange, product, onSubmit }) {
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    setForm(EMPTY);
  }, [product, open]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const computedCbm = (form.carton_length && form.carton_width && form.carton_height)
    ? ((parseFloat(form.carton_length) * parseFloat(form.carton_width) * parseFloat(form.carton_height)) / 1_000_000).toFixed(6)
    : form.cbm;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      price: form.price === "" ? null : parseFloat(form.price),
      pcs_per_ctn: form.pcs_per_ctn === "" ? null : parseInt(form.pcs_per_ctn, 10),
      cbm: computedCbm === "" ? null : parseFloat(computedCbm),
      qty: form.qty === "" ? null : parseInt(form.qty, 10),
      carton_length: form.carton_length === "" ? null : parseFloat(form.carton_length),
      carton_width: form.carton_width === "" ? null : parseFloat(form.carton_width),
      carton_height: form.carton_height === "" ? null : parseFloat(form.carton_height),
      purchase_price: form.purchase_price === "" ? null : parseFloat(form.purchase_price),
      gross_weight: form.gross_weight === "" ? null : parseFloat(form.gross_weight),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="variant-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-xl tracking-tight text-gray-900">Add Price Variant</DialogTitle>
          <DialogDescription className="text-gray-500">{product?.description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map(([key, label, type = "text"]) => (
            <div key={key}>
              <Label className="text-xs font-medium text-gray-600">{label}</Label>
              <Input
                data-testid={`variant-form-${key}`}
                type={type}
                step={type === "number" ? "any" : undefined}
                value={form[key]}
                onChange={(e) => update(key, e.target.value)}
                className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
              />
            </div>
          ))}
          <div>
            <Label className="text-xs font-medium text-gray-600">CBM (auto-computed from L×W×H)</Label>
            <Input
              data-testid="variant-form-cbm-computed"
              readOnly
              value={computedCbm}
              placeholder="Fill in Carton Length/Width/Height to auto-compute"
              className="mt-1.5 rounded-lg border-gray-300 bg-gray-100 text-gray-500"
            />
          </div>
          <DialogFooter className="sm:col-span-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg border-gray-300">Cancel</Button>
            <Button type="submit" data-testid="variant-form-submit" className="rounded-lg bg-[#1677ff] hover:bg-[#0958d9] transition-colors">
              Add Variant
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
